import React, { useEffect, useState } from "react";
import { Page, Document, Image, StyleSheet, Text, View, Font } from "@react-pdf/renderer";
import { Canvg } from "canvg";
import { pdfLogo, formatPrice, truncateToTwoDecimal, formatPdfPhone, formatLongDate } from "utils/common";
import chunk from "lodash/chunk";
import Roboto from "resources/fonts/Roboto-Regular.ttf";
import RobotoBold from "resources/fonts/Roboto-Bold.ttf";

Font.register({ family: "Roboto", src: Roboto });
Font.register({ family: "Roboto-Bold", src: RobotoBold });

const styles = StyleSheet.create({
  noBorder: {
    border: "0",
  },
  header: {
    container: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-end",
      marginBottom: 25,
    },
    imageWrapper: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
    image: {
      height: 50,
      marginRight: "34px",
    },
  },
  extraSmallText: {
    fontSize: 8,
    marginBottom: 8,
  },
  smallText: {
    fontSize: 10,
    marginBottom: 2,
  },
  largeText: {
    fontSize: 16,
    marginBottom: 2,
  },
  textBold: {
    fontFamily: "Roboto-Bold",
    fontWeight: 700,
  },
  colorPrimary: {
    color: "#0076C2",
  },
  body: {
    border: "0",
    padding: 22,
    display: "flex",
    flexGrow: 1,
  },
  customerName: {
    fontSize: 30,
    fontFamily: "Roboto-Bold",
    fontWeight: 700,
    marginBottom: 8,
  },
  coverLetterSection: {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
    marginTop: -50,
  },
  createdFor: {
    fontSize: 22,
    marginTop: 24,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  footer: {
    fontSize: 6,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    position: "absolute",
    bottom: 0,
    width: "100%",
    left: 0,
    padding: "0 22",
    marginBottom: 12,
  },
  tableHeaderWrapper: {
    paddingLeft: 12,
    paddingRight: 12,
    backgroundColor: "#E9F3FD",
    borderRadius: 8,
    border: "1px solid #F0F0F0",
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  tableHeaderRow: {
    display: "flex",
    flexDirection: "row",
    paddingVertical: 3,
    gap: 25,
  },
  tableMarketRow: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: 25,
  },
  tableMarketText: {
    textAlign: "right",
  },
  row: {
    height: 19,
    display: "flex",
    flexDirection: "row",
    border: "1px solid #F0F0F0",
    borderTop: 0,
    paddingVertical: 3,
    paddingHorizontal: 12,
    gap: 25,
  },
  evenRow: {
    backgroundColor: "#F7F7F7",
  },
  oddRow: {
    backgroundColor: "#FFF",
  },
  marketRow: {
    display: "flex",
    flexDirection: "row",
    gap: 25,
    width: "100%",
  },
  width60: {
    width: 60,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    border: "1px solid #F0F0F0",
    borderTop: 0,
    paddingVertical: 3,
    paddingHorizontal: 12,
    gap: 25,
  },
  subtotalRow: {
    backgroundColor: "#EAFFE6",
    borderTop: 0,
  },
  textAlignRight: {
    textAlign: "right",
  },
  width232: {
    width: 232,
  },
});

const svgToDataUri = async (svgString) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  const v = Canvg.fromString(context, svgString.trim());
  await v.render();
  return canvas.toDataURL("image/png");
};

const ProgramPdfDocument = (props) => {
  const {
    programDetailsData,
    hasCoverLetter,
    orderedMarkets,
    tableItemsColumnData,
    tablePricesData,
    totalPriceForProduct,
  } = props;

  const [customerLogo, setCustomerLogo] = useState(programDetailsData.customerLogo);
  useEffect(() => {
    if (programDetailsData.customerLogo?.startsWith("data:image/svg+xml;base64,")) {
      const svgString = atob(programDetailsData.customerLogo.replace("data:image/svg+xml;base64,", ""));
      svgToDataUri(svgString).then((converted) => setCustomerLogo(converted));
    } else {
      setCustomerLogo(programDetailsData.customerLogo);
    }
  }, [programDetailsData.customerLogo]);

  let rowCounter = 0;
  let tableElementCount = 0;
  let columnWidth = styles.width60;
  const sectionMapping = {
    1: "Soft Surface",
    2: "Hard Surface",
    3: "A la Carte",
  };
  const renderSectionsForMarkets = (markets) => {
    const MAX_MARKET_SIZE = 7;
    if (markets.length < MAX_MARKET_SIZE) {
      columnWidth = { width: `${100 / markets.length}%`, ...styles.textAlignRight };
    }
    const marketChunks = Array.from({ length: Math.ceil(markets.length / MAX_MARKET_SIZE) }, (_, index) =>
      markets.slice(index * MAX_MARKET_SIZE, index * MAX_MARKET_SIZE + MAX_MARKET_SIZE),
    );

    return marketChunks.map((marketChunk, chunkIndex) => {
      tableElementCount = 0;
      const softSurfaceItems = tableItemsColumnData.filter(({ typeId }) => typeId === 1);
      const hardSurfaceItems = tableItemsColumnData.filter(({ typeId }) => typeId === 2);
      const aLaCarteItems = tableItemsColumnData.filter(({ typeId }) => typeId === 3);

      const key = marketChunk.reduce((acc, { marketId }) => `${acc}_${marketId}`, `marketchunk_`);
      return (
        <View break={hasCoverLetter || chunkIndex > 0} key={key}>
          {softSurfaceItems.length > 0 && (
            <>
              {renderSectionHeader(sectionMapping[1], chunkIndex > 0)}
              {softSurfaceItems.map((item) => {
                rowCounter = 0;
                return (
                  <View wrap={false} style={{ marginBottom: 20 }} key={marketChunk.key}>
                    {renderTableHeader(marketChunk, item)}
                    {renderProductRows(marketChunk, item)}
                    {renderLaborCodeRows(marketChunk, item)}
                    {renderSubtotalRow(marketChunk, item)}
                    {renderPadRows(marketChunk, item)}
                  </View>
                );
              })}
            </>
          )}

          {hardSurfaceItems.length > 0 &&
            hardSurfaceItems.map((item) => {
              rowCounter = 0;
              tableElementCount++;
              return (
                <View wrap={false} style={{ marginBottom: 20 }}>
                  {tableElementCount === 1 && renderSectionHeader(sectionMapping[2], chunkIndex > 0)}
                  {renderTableHeader(marketChunk, item)}
                  {renderProductRows(marketChunk, item)}
                  {renderLaborCodeRows(marketChunk, item)}
                  {renderSubtotalRow(marketChunk, item)}
                </View>
              );
            })}

          {aLaCarteItems.length > 0 &&
            chunk(aLaCarteItems, 18).map((items, index) => (
              <View
                wrap={false}
                key={items.reduce((acc, { productId }) => `${acc}_${productId}`, `${key}_alacartItems_`)}
              >
                {renderSectionHeader(sectionMapping[3], chunkIndex > 0 || index > 0)}
                {renderTableHeader(marketChunk)}
                {items.map((item) => (
                  <View wrap={false} key={item.productId}>
                    {renderProductRows(marketChunk, item)}
                    {renderLaborCodeRows(marketChunk, item)}
                  </View>
                ))}
              </View>
            ))}
        </View>
      );
    });
  };

  const renderSectionHeader = (header, hasMoreThanOnePage) => (
    <>
      <Text style={{ ...styles.smallText, ...styles.textBold, marginBottom: 2 }}>
        {`${header} ${hasMoreThanOnePage ? "(continued)" : ""}`}
      </Text>
      {header !== "A la Carte" && <Text style={styles.extraSmallText}>Prices in table per square foot (sq/ft)</Text>}
    </>
  );

  const renderTableHeader = (marketChunk, categoryItem) => (
    <View style={styles.tableHeaderWrapper} wrap={false}>
      <View style={styles.tableHeaderRow}>
        <Text style={{ ...styles.width232, ...styles.smallText, ...styles.textBold, textTransform: "uppercase" }}>
          {categoryItem ? categoryItem.categoryName : ""}
        </Text>
        <View style={styles.tableMarketRow}>
          {marketChunk?.map((market) => (
            <Text
              key={market.marketId}
              style={{ ...styles.tableMarketText, ...columnWidth, ...styles.smallText, ...styles.textBold }}
            >
              {market.name}
            </Text>
          ))}
        </View>
      </View>
    </View>
  );

  const renderProductRows = (marketChunk, categoryItem) => {
    return categoryItem.products?.map((productItem, productIndex) => {
      if (productItem.name.startsWith("Pad")) return null;
      rowCounter = rowCounter + 1;
      const rowStyle = rowCounter % 2 === 0 ? styles.evenRow : styles.oddRow;
      return (
        <View style={[styles.row, rowStyle]} key={productIndex} wrap={false}>
          <Text style={{ ...styles.width232, ...styles.smallText }}>
            {productItem.name} {productItem.unitOfMeasurement && `(${productItem.unitOfMeasurement})`}
          </Text>
          <View style={styles.marketRow}>
            {marketChunk?.map((market, marketIndex) => {
              return (
                <Text key={marketIndex} style={{ ...columnWidth, ...styles.smallText, ...styles.textAlignRight }}>
                  {(tablePricesData[market.marketId] ?? [])
                    .filter(({ productId, lineItemId, price }) => {
                      return (
                        productId == productItem.productId && lineItemId == productItem.lineItemId && price !== null
                      );
                    })
                    .map((secKey) => `$${formatPrice(truncateToTwoDecimal(secKey.price))}`)}
                </Text>
              );
            })}
          </View>
        </View>
      );
    });
  };

  const renderLaborCodeRows = (marketChunk, categoryItem) => {
    return categoryItem.laborCodes?.map(
      ({ lineItemId, name, laborCode, laborCodeDesc, unitOfMeasurement, productId }) => {
        rowCounter = rowCounter + 1;
        const rowStyle = rowCounter % 2 === 0 ? styles.evenRow : styles.oddRow;
        return (
          <View style={{ ...styles.row, ...styles.smallText, ...rowStyle }} key={lineItemId} wrap={false}>
            <Text style={{ ...styles.width232, textTransform: "capitalize" }}>
              {name?.toLowerCase() || `${laborCode} - ${laborCodeDesc?.toLowerCase()}`}
              {unitOfMeasurement && ` (${unitOfMeasurement})`}
            </Text>
            <View style={styles.marketRow} wrap={false}>
              {marketChunk?.map((market) => (
                <Text key={market.marketId} style={{ ...columnWidth, ...styles.textAlignRight }}>
                  {(tablePricesData[market.marketId] ?? [])
                    .filter(
                      (secKey) =>
                        secKey.productId == productId && secKey.lineItemId == lineItemId && secKey.price !== null,
                    )
                    .map((secKey) => `$${formatPrice(truncateToTwoDecimal(secKey.price))}`)}
                </Text>
              ))}
            </View>
          </View>
        );
      },
    );
  };

  const renderSubtotalRow = (marketChunk, categoryItem) => (
    <View style={[styles.row, styles.subtotalRow]} wrap={false}>
      <Text
        style={{ ...styles.smallText, ...styles.textBold, ...styles.width232 }}
      >{`Subtotal ${categoryItem.categoryId == 1 ? "w/o Pad" : ""}`}</Text>
      <View style={styles.marketRow}>
        {marketChunk?.map((market, marketIndex) => (
          <Text
            style={{ ...styles.smallText, ...styles.textBold, ...columnWidth, ...styles.textAlignRight }}
            key={marketIndex}
          >
            {totalPriceForProduct && Object.keys(totalPriceForProduct)?.some((x) => x == categoryItem.productId)
              ? Object.keys(totalPriceForProduct)?.map((productId) => {
                  if (productId == categoryItem.productId) {
                    return `$${formatPrice(truncateToTwoDecimal(totalPriceForProduct[productId][market.marketId] || 0))}`;
                  }
                  return null;
                })
              : "$0.00"}
          </Text>
        ))}
      </View>
    </View>
  );

  const renderPadRows = (marketChunk, categoryItem) => {
    return categoryItem.products?.map((productItem, productIndex) => {
      if (productItem.name.startsWith("Pad")) {
        rowCounter = rowCounter + 1;
        const rowStyle = rowCounter % 2 === 0 ? styles.evenRow : styles.oddRow;
        return (
          <>
            <View style={[styles.row, rowStyle]} key={productIndex} wrap={false}>
              <Text style={[styles.smallText, styles.width232]}>{productItem.name}</Text>
              <View style={styles.marketRow}>
                {marketChunk?.map((market, marketIndex) => (
                  <Text style={{ ...columnWidth, ...styles.smallText, ...styles.textAlignRight }} key={marketIndex}>
                    {tablePricesData[market.marketId]?.length !== 0 &&
                      tablePricesData[market.marketId]?.map((secKey, secKeyIndex) => {
                        if (secKey.productId == productItem.productId && secKey.lineItemId == productItem.lineItemId) {
                          return secKey.price !== null ? `$${formatPrice(truncateToTwoDecimal(secKey.price))}` : "";
                        }
                        return "";
                      })}
                  </Text>
                ))}
              </View>
            </View>
            {renderPadSubtotalRow(marketChunk, categoryItem, productItem, tablePricesData)}
          </>
        );
      }
      return null;
    });
  };

  const renderPadSubtotalRow = (marketChunk, categoryItem, productItem, tablePricesData) => (
    <View style={[styles.row, styles.subtotalRow]} wrap={false}>
      <Text style={{ ...styles.smallText, ...styles.textBold, ...styles.width232 }}>Subtotal w/ Pad</Text>
      <View style={styles.marketRow}>
        {marketChunk?.map((market, marketIndex) => {
          const padItem = tablePricesData[market.marketId]?.find(
            (secKey) => secKey.productId == productItem.productId && secKey.lineItemId == productItem.lineItemId,
          );
          if (padItem) {
            return (
              <Text
                style={{ ...styles.smallText, ...styles.textBold, ...columnWidth, ...styles.textAlignRight }}
                key={marketIndex}
              >
                {totalPriceForProduct && Object.keys(totalPriceForProduct)?.some((x) => x == categoryItem.productId)
                  ? Object.keys(totalPriceForProduct)?.map((productId) => {
                      if (productId == categoryItem.productId) {
                        const subtotalInCents = Math.round(
                          totalPriceForProduct?.[padItem?.productId]?.[market?.marketId] * 100,
                        );
                        const padTotalInCents = padItem?.price * 100;
                        const subTotalWithPad = (subtotalInCents + padTotalInCents) / 100;
                        return `$${formatPrice(truncateToTwoDecimal(subTotalWithPad))}`;
                      }
                      return "";
                    })
                  : ""}
              </Text>
            );
          } else {
            return (
              <Text
                style={{ ...styles.smallText, ...styles.textBold, ...columnWidth, ...styles.textAlignRight }}
              >{`$${formatPrice(truncateToTwoDecimal(parseFloat(totalPriceForProduct[productItem?.productId]?.[market.marketId] || 0)))}`}</Text>
            );
          }
        })}
      </View>
    </View>
  );

  const renderCoverLetter = () => (
    <View style={{ ...styles.body }}>
      <View style={styles.coverLetterSection}>
        <Text style={styles.customerName}>{programDetailsData?.coverLetterTitle}</Text>
        <Text>{formatLongDate(programDetailsData?.updatedDate ?? programDetailsData?.createdDate)}</Text>
        <View style={styles.createdFor}>
          <Text style={{ ...styles.textBold }}>Created For:</Text>
          <Text style={{ fontSize: 20 }}>{programDetailsData?.toData}</Text>
        </View>
      </View>
    </View>
  );

  return (
    <>
      <Document style={styles.noBorder}>
        <Page style={styles.body} orientation="landscape">
          <View style={styles.header} fixed>
            <View style={styles.header.container}>
              <View style={styles.header.imageWrapper}>
                <Image style={styles.header.image} src={() => pdfLogo} />
                {programDetailsData?.customerLogo ? (
                  <Image style={styles.header.image} src={customerLogo} />
                ) : (
                  <Text style={[styles.largeText, styles.textBold]}>{programDetailsData?.customerName}</Text>
                )}
              </View>
              <View
                style={{
                  ...styles.extraSmallText,
                  flexGrow: 1,
                  paddingTop: 12,
                  alignItems: "flex-end",
                  height: "100%",
                }}
              >
                <Text style={styles.textBold}>
                  {programDetailsData?.fromUserName + ", " + programDetailsData?.fromUserTitle}
                </Text>
                <Text>{programDetailsData?.fromUserEmail}</Text>
                <Text>{formatPdfPhone(programDetailsData?.fromUserPhoneNumber)}</Text>
              </View>
            </View>
          </View>
          {hasCoverLetter && renderCoverLetter()}

          {renderSectionsForMarkets(orderedMarkets)}

          <View style={styles.footer} fixed>
            <Text>Program ID #{programDetailsData?.programId}</Text>
            <Text>
              Updated on{" "}
              {new Date(programDetailsData?.updatedDate ?? programDetailsData?.createdDate).toLocaleDateString("en-US")}
            </Text>
            <Text>Expiration Date {new Date(programDetailsData?.expirationDate).toLocaleDateString("en-US")}</Text>
            <Text>For {programDetailsData?.customerName}</Text>
            <Text>
              This program is not a contract and is subject to and conditioned upon final approval by Sherwin-Williams.
            </Text>
          </View>
        </Page>
      </Document>
    </>
  );
};

export default ProgramPdfDocument;
